import { Link } from 'react-router-dom'
import type { ReportTrial } from '../../../api/playerReports'
const n = (v: number) => v.toLocaleString('es-AR', { maximumFractionDigits: 1 })
export const trialUrl = (t: { session_id: string; id: string }) =>
  `/data/reports/sessions/${encodeURIComponent(t.session_id)}/trials/${encodeURIComponent(t.id)}`

export function TrialTimeline({
  trials,
  single = false,
}: {
  trials: ReportTrial[]
  single?: boolean
}) {
  const maxTime = Math.max(
    1,
    ...trials.flatMap((t) => [t.deadline_s ?? 0, t.response_s ?? 0]),
  )
  return (
    <section className="report-section" aria-labelledby="execution-heading">
      <div className="report-section-heading">
        <div>
          <p className="report-eyebrow">
            {single ? 'La ventana de acción' : 'Jugada por jugada'}
          </p>
          <h2 id="execution-heading">Decidir y ejecutar a tiempo</h2>
        </div>
        <p className="report-caption">El reloj empieza cuando podés actuar.</p>
      </div>
      <div className="report-key">
        <span>
          <i className="report-key-window" />
          Tiempo disponible
        </span>
        <span>
          <i className="report-key-action" />
          Momento de ejecución
        </span>
      </div>
      <div className="report-trial-list">
        {trials.map((t) => {
          const held = t.outcome === 'held'
          const good = ['goal', 'correct', 'held', 'success'].includes(
            t.outcome,
          )
          const pending = ['unknown', 'aborted', 'completed'].includes(
            t.outcome,
          )
          return (
            <article className="report-trial" key={t.id}>
              <div className="report-trial-label">
                <span className="report-trial-number">
                  {String(t.number).padStart(2, '0')}
                </span>
                <div>
                  <h3>
                    {single ? (
                      t.scenario_label
                    ) : (
                      <Link to={trialUrl(t)}>
                        {t.label} · {t.scenario_label}
                      </Link>
                    )}
                  </h3>
                  <p className="report-caption">
                    {t.context_label || 'Contexto no registrado'}
                  </p>
                </div>
                <span
                  className={`report-result ${good ? 'is-good' : pending ? '' : 'is-review'}`}
                >
                  {t.outcome_label}
                </span>
              </div>
              <div className="report-time-detail">
                {t.deadline_s !== null ? (
                  <>
                    <div
                      className="report-time-track"
                      role="img"
                      aria-label={`Tiempo disponible ${n(t.deadline_s)} segundos. ${held ? 'Espera correcta.' : t.response_s === null ? 'Sin tiempo de ejecución registrado.' : `Ejecutaste a los ${n(t.response_s)} segundos.`}`}
                    >
                      <div
                        className="report-time-window"
                        style={{ width: `${(100 * t.deadline_s) / maxTime}%` }}
                      />
                      {t.response_s !== null && !held && (
                        <span
                          className="report-time-action"
                          style={{ left: `${(100 * t.response_s) / maxTime}%` }}
                        />
                      )}
                    </div>
                    <div className="report-time-values">
                      <span>0 s</span>
                      <strong>{n(t.deadline_s)} s disponibles</strong>
                    </div>
                  </>
                ) : (
                  <p className="report-caption">
                    No quedó registrado el tiempo disponible.
                  </p>
                )}
                <p className="report-caption">
                  {held
                    ? 'Esperaste cuando correspondía. No había que ejecutar un pase.'
                    : t.response_s !== null
                      ? `Ejecutaste a los ${n(t.response_s)} s${t.deadline_s !== null && t.response_s <= t.deadline_s ? ` · ${n(t.deadline_s - t.response_s)} s antes del límite` : ''}.`
                      : t.outcome === 'timeout'
                        ? 'Se cerró la ventana sin una resolución a tiempo.'
                        : 'Sin tiempo de ejecución registrado.'}
                </p>
              </div>
              {!single && (
                <Link className="report-review-link" to={trialUrl(t)}>
                  Revisar jugada →
                </Link>
              )}
            </article>
          )
        })}
      </div>
      <p className="report-caption">
        Todas las barras usan la misma escala de tiempo. Ejecutar antes no
        implica elegir mejor. Preparación y vuelo de la pelota quedan fuera.
      </p>
    </section>
  )
}
